import { useCallback, useEffect, useMemo } from 'react';
import {
    buildCertificationFields,
    buildCompanyFields,
    buildContactFields
} from 'v2/layouts/OrganizationWorkspaceLayout/components/OrganizationDetailsSection/constants';
import useTranslation from 'v2/layouts/OrganizationWorkspaceLayout/hooks/useTranslation';

import useCurrentOrganization from 'hooks/useCurrentOrganization';
import useNavigation from 'hooks/useNavigation';
import usePageRoutes from 'hooks/usePageRoutes';
import useRoles from 'hooks/useRoles';

const useOrganizationDetails = () => {
    const { canManageOrganizations } = useRoles();
    const {
        currentOrganization,
        fetchPersonOfContact,
        personOfContact,
        refreshCurrentOrganization
    } = useCurrentOrganization();
    const { navigate } = useNavigation();
    const { editCertificationsPage, editCompanyDetailsPage, editPersonOfContactPage } =
        usePageRoutes();

    const {
        ADDRESS,
        CERTIFICATE_OF_AUTHORIZATION_NUMBER,
        CITY,
        COMPANY_NAME,
        CONTACT_NAME,
        EMAIL,
        FAA_DOCKET_NUMBER,
        FAA_EXEMPTION_NUMBER,
        NOT_PROVIDED,
        PHONE,
        STATE,
        ZIP
    } = useTranslation();

    useEffect(() => {
        const abortController = new AbortController();

        void refreshCurrentOrganization(abortController.signal);
        // This call was missing the same cancel signal used above — without it,
        // a late response here could overwrite fresh data after this screen closes.
        void fetchPersonOfContact(false, abortController.signal);

        return () => abortController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const companyFields = useMemo(
        () =>
            buildCompanyFields(
                currentOrganization,
                {
                    address: ADDRESS,
                    city: CITY,
                    companyName: COMPANY_NAME,
                    phone: PHONE,
                    state: STATE,
                    zip: ZIP
                },
                NOT_PROVIDED
            ),
        [ADDRESS, CITY, COMPANY_NAME, NOT_PROVIDED, PHONE, STATE, ZIP, currentOrganization]
    );

    const contactFields = useMemo(
        () =>
            buildContactFields(
                personOfContact,
                { contactName: CONTACT_NAME, email: EMAIL, phone: PHONE },
                NOT_PROVIDED
            ),
        [CONTACT_NAME, EMAIL, NOT_PROVIDED, PHONE, personOfContact]
    );

    const certificationFields = useMemo(
        () =>
            buildCertificationFields(
                currentOrganization,
                {
                    certificateOfAuthorizationNumber: CERTIFICATE_OF_AUTHORIZATION_NUMBER,
                    faaDocketNumber: FAA_DOCKET_NUMBER,
                    faaExemptionNumber: FAA_EXEMPTION_NUMBER
                },
                NOT_PROVIDED
            ),
        [
            CERTIFICATE_OF_AUTHORIZATION_NUMBER,
            FAA_DOCKET_NUMBER,
            FAA_EXEMPTION_NUMBER,
            NOT_PROVIDED,
            currentOrganization
        ]
    );

    const handleEditCompany = useCallback(
        () => navigate(editCompanyDetailsPage),
        [editCompanyDetailsPage, navigate]
    );

    const handleEditContact = useCallback(
        () => navigate(editPersonOfContactPage),
        [editPersonOfContactPage, navigate]
    );

    const handleEditCertifications = useCallback(
        () => navigate(editCertificationsPage),
        [editCertificationsPage, navigate]
    );

    return {
        canEdit: Boolean(canManageOrganizations),
        certificationFields,
        companyFields,
        contactFields,
        handleEditCertifications,
        handleEditCompany,
        handleEditContact
    };
};

export default useOrganizationDetails;
